import { createMemo, Index, Show } from "solid-js";
import { useTranslation } from "../state/i18n";
import uuid from "../utils/uuid";
import DraggableList from "../pages/customer/dnd/DraggableList";
import { getPanelScrollContainerId } from "../components/panels/FormEditorSidePanel";
import { ImageOptionProperty, TextOptionProperty } from "../components/optionProperty/OptionProperty";
import { isNotEmpty } from "../utils/stringUtil";
import Popover from "../components/popover/Popover";
import { TextAndIconButton } from "../components/button/Button";
import Text from "../components/text/Text";
import { TextInput } from "../components/inputs/TextInput";
import { MAX_OPTIONS } from "../constants";
import InlineMessage from "../components/inlineMessage/InlineMessage";
import type { Issue } from "../state/validation";
import { isNil } from "lodash-es";
import type { FormScope } from "../state/formVersion";
import Input from "../components/inputs/Input";
import { findError } from "../utils/validationUtil";
import OptionButton from "../components/button/OptionButton";

export type OptionItem = {
  key: string;
  id: string;
  name?: string | null;
  imgResource?: string | null;
  email_label?: string | null;
};

type OptionsPropertyInputProps = {
  options: OptionItem[];
  onChange: (options: OptionItem[]) => void;
  variant: "text" | "image";
  error: string | undefined;
  nestedErrors: Issue[] | undefined;
  currentScope: FormScope;
};

export const OptionsPropertyInput = (props: OptionsPropertyInputProps) => {
  const { t } = useTranslation();

  const updateOption = (key: string, updater: (o: OptionItem) => OptionItem) =>
    props.onChange(props.options.map((o) => (o.key === key ? updater(o) : o)));

  const removeOption = (key: string) => props.onChange(props.options.filter((o) => o.key !== key));

  const addOption = () => {
    const label = `${t(($) => $.FIELD_PROPERTY_OPTIONS_ADD_PREFIX)} ${props.options.length + 1}`;
    // Always set the key: the field can be switched to images later, and a missing key fails validation with a generic error
    props.onChange([...props.options, { key: uuid(), id: label, name: label, imgResource: undefined }]);
  };

  return (
    <Input label={t(($) => $.FIELD_PROPERTY_OPTIONS_LABEL)} required errorMessage={props.error}>
      {() => (
        <div class="flex flex-col gap-y-1">
          <ul class="flex flex-col gap-y-1">
            <DraggableList
              scrollContainer={document.getElementById(getPanelScrollContainerId(props.currentScope)) as HTMLDivElement}
              monitorDraggableType="OPTION"
              items={props.options.map((opt) => ({ id: opt.id }))}
              onDrop={(sourceData, targetIndex) => {
                const newArr = [...props.options];
                const element = newArr.splice(sourceData.index, 1)[0];
                newArr.splice(targetIndex, 0, element);
                props.onChange(newArr);
              }}
            >
              <Index each={props.options}>
                {(option, index) => {
                  const issues = createMemo(() =>
                    props.nestedErrors?.filter((issue) => (issue.path ?? []).some((p) => p.key === index)),
                  );

                  const imageError = createMemo(() => findError(issues(), "imgResource"));
                  const nameError = createMemo(() => findError(issues(), "name"));
                  const valueError = createMemo(() => findError(issues(), "id"));
                  const pdfError = createMemo(() => findError(issues(), "email_label"));

                  return (
                    <li class="flex w-full gap-x-1">
                      <Show
                        when={props.variant === "image"}
                        fallback={
                          <TextOptionProperty
                            id={option().id}
                            index={index}
                            isLast={index === props.options.length - 1}
                            value={option().name ?? ""}
                            hasError={!isNil(nameError())}
                            onChange={(value) => {
                              const newValue = isNotEmpty(value) ? value : (option().name ?? "");
                              updateOption(option().key, (o) => {
                                const updated: OptionItem = { ...o, name: newValue };
                                if (o.name === o.id) {
                                  updated.id = newValue;
                                  if (o.id === o.email_label) {
                                    updated.email_label = newValue;
                                  }
                                }
                                return updated;
                              });
                            }}
                            action={{
                              icon: "trash",
                              ariaLabel: t(($) => $.FIELD_PROPERTY_OPTIONS_DELETE_ARIA, { name: option().name }),
                              onClick: () => removeOption(option().key),
                            }}
                          />
                        }
                      >
                        <ImageOptionProperty
                          id={option().id}
                          index={index}
                          isLast={index === props.options.length - 1}
                          resourceId={option().imgResource}
                          hasError={!isNil(imageError())}
                          alt={option().id}
                          onChange={(value) => {
                            const newValue = isNotEmpty(value) ? value : (option().imgResource ?? "");
                            updateOption(option().key, (o) => ({ ...o, imgResource: newValue }));
                          }}
                          action={{
                            icon: "trash",
                            ariaLabel: t(($) => $.FIELD_PROPERTY_OPTIONS_DELETE_ARIA, { name: option().id }),
                            onClick: () => removeOption(option().key),
                          }}
                        />
                      </Show>

                      <Popover
                        aria-label={t(($) => $.FIELD_PROPERTY_OPTIONS_POPOVER_TITLE)}
                        variant="md"
                        positioning={{ placement: "bottom-end" }}
                        Trigger={(arkProps) => (
                          <OptionButton
                            title={t(($) => $.FIELD_PROPERTY_OPTIONS_EDIT_ARIA, {
                              name: props.variant === "text" ? option().name : option().id,
                            })}
                            icon="cog"
                            disabled={false}
                            hasError={!isNil(valueError()) || !isNil(pdfError())}
                            htmlProps={arkProps}
                          />
                        )}
                        Title={() => (
                          <Text
                            as="h3"
                            truncate
                            text={t(($) => $.FIELD_PROPERTY_OPTIONS_POPOVER_TITLE)}
                            class="text-header-md text-primary"
                          />
                        )}
                      >
                        <div class="flex flex-col gap-y-4 pt-4">
                          <TextInput
                            label={t(($) => $.FIELD_PROPERTY_OPTIONS_VALUE_LABEL)}
                            value={option().id}
                            onBlur={(e) => {
                              updateOption(option().key, (o) => {
                                const updated: OptionItem = { ...o, id: e.target.value };
                                if (o.id === o.email_label) {
                                  updated.email_label = e.target.value;
                                }
                                return updated;
                              });
                            }}
                            required
                            errorMessage={valueError()}
                            info={{
                              title: t(($) => $.FIELD_PROPERTY_OPTIONS_VALUE_LABEL),
                              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL),
                              description: t(($) => $.FIELD_PROPERTY_OPTIONS_VALUE_DESCRIPTION),
                            }}
                          />
                          <TextInput
                            label={t(($) => $.FIELD_PROPERTY_OPTIONS_PDF_LABEL)}
                            value={option().email_label ?? ""}
                            onBlur={(e) => updateOption(option().key, (o) => ({ ...o, email_label: e.target.value }))}
                            errorMessage={pdfError()}
                            info={{
                              title: t(($) => $.FIELD_PROPERTY_OPTIONS_PDF_LABEL),
                              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL),
                              description: t(($) => $.FIELD_PROPERTY_OPTIONS_PDF_DESCRIPTION),
                            }}
                          />
                        </div>
                      </Popover>
                    </li>
                  );
                }}
              </Index>
            </DraggableList>
          </ul>

          <Show
            when={props.options.length < MAX_OPTIONS}
            fallback={
              <InlineMessage variant="warning" label={t(($) => $.FIELD_PROPERTY_OPTIONS_MAX, { max: MAX_OPTIONS })} />
            }
          >
            <TextAndIconButton
              variant="light"
              size="large"
              block
              icon="plus"
              text={t(($) => $.FIELD_PROPERTY_OPTIONS_ADD_BUTTON)}
              onClick={addOption}
            />
          </Show>
        </div>
      )}
    </Input>
  );
};
